// webapp/src/jobs/JobHub.tsx
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { startVisiblePolling } from '../lib/visiblePolling';
import { apiFetch } from '../api/http';
import useSessionStore from '../state/session';
import { getSiteAccessState } from '../state/siteAccess';
import { fetchVenueResearchRuns, subscribeToVenueResearchRuns } from '../data/venueResearch';

export type JobKind = 'venue_research' | 'item_lookup';

export type JobRow = {
  job_id: string;
  job_kind: JobKind;
  venue_query?: string | null;
  item_make?: string | null;
  item_model?: string | null;
  item_year?: string | null;
  item_summary?: string | null;
  item_id?: string | null;
  item_ai_run_id?: string | null;
  status: 'queued' | 'running' | 'done' | 'error' | 'cancelling' | 'cancelled';
  progress: string | null;
  task: string | null;
  model: string | null;
  updated_at: string;
  last_heartbeat?: string | null;
  requested_by?: string | null;
  error?: string | null;
  job_log?: string | null;
  venue_id?: string | null;
  overwrite_existing?: boolean | null;
};

export type JobRowUpdate = Partial<JobRow> & { job_id: string };

type Ctx = {
  jobs: JobRow[];
  activeIndex: number;
  next(): void;
  prev(): void;
  cancel(job: JobRow): Promise<boolean>;
  dismiss(jobId: string): Promise<void>;
  setJobs(j: JobRow[] | ((prev: JobRow[]) => JobRow[])): void;
  upsertJob?(job: JobRowUpdate): void; // optional helper; we'll add it below
};
const JobsCtx = createContext<Ctx | null>(null);

const ACTIVE_STATES = new Set(['queued','running','cancelling']);
const STALE_MS = 60_000;
const MAX_STORED_JOBS = 20;

function normaliseJob(input: JobRowUpdate, existing?: JobRow): JobRow {
  const merged: JobRowUpdate & { job_id: string } = { ...(existing || {}), ...input };
  const kind: JobKind = (merged.job_kind ?? existing?.job_kind ?? 'venue_research') as JobKind;
  const status = (merged.status ?? existing?.status ?? 'queued') as JobRow['status'];
  const task = merged.task ?? existing?.task ?? (kind === 'item_lookup' ? 'lookup' : null);
  return {
    job_id: merged.job_id,
    job_kind: kind,
    venue_query: merged.venue_query ?? existing?.venue_query ?? null,
    item_make: merged.item_make ?? existing?.item_make ?? null,
    item_model: merged.item_model ?? existing?.item_model ?? null,
    item_year: merged.item_year ?? existing?.item_year ?? null,
    item_summary: merged.item_summary ?? existing?.item_summary ?? null,
    item_id: merged.item_id ?? existing?.item_id ?? null,
    item_ai_run_id: merged.item_ai_run_id ?? existing?.item_ai_run_id ?? null,
    status,
    progress: merged.progress ?? existing?.progress ?? null,
    task,
    model: merged.model ?? existing?.model ?? null,
    updated_at: merged.updated_at ?? existing?.updated_at ?? new Date().toISOString(),
    last_heartbeat: merged.last_heartbeat ?? existing?.last_heartbeat ?? null,
    requested_by: merged.requested_by ?? existing?.requested_by ?? null,
    error: merged.error ?? existing?.error ?? null,
    job_log: merged.job_log ?? existing?.job_log ?? null,
    venue_id: merged.venue_id ?? existing?.venue_id ?? null,
    overwrite_existing: merged.overwrite_existing ?? existing?.overwrite_existing ?? null,
  };
}

function isActive(j: JobRow) {
  if (ACTIVE_STATES.has(j.status)) return true;
  // Prefer last_heartbeat over updated_at for staleness decisions
  const t = new Date(j.last_heartbeat ?? j.updated_at).getTime();
  return Date.now() - t < STALE_MS;
}

export const JobHubProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const session = useSessionStore(s => s.session);
  const sessionReady = useSessionStore(s => s.sessionReady);
  const securityLevel = useSessionStore(s => s.securityLevel);
  const allowedLocationIds = useSessionStore(s => s.allowedLocationIds);
  const siteAccess = getSiteAccessState({
    sessionReady,
    securityLevel,
    allowedLocationIds,
  });
  const canAccessAppData = Boolean(
    session?.user?.id
    && sessionReady
    && !siteAccess.isBlocked
  );
  const [jobs, setJobs] = useState<JobRow[]>(() => {
    try {
      const raw = sessionStorage.getItem('vrr.active');
      if (!raw) return [];
      const arr: JobRowUpdate[] = JSON.parse(raw);
      return arr.map(job => normaliseJob(job)).filter(isActive).slice(0, MAX_STORED_JOBS);
    } catch { return []; }
  });
  
  const [activeIndex, setActiveIndex] = useState(0);
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const pollingRef = useRef<ReturnType<typeof startVisiblePolling> | null>(null);
  const hasActiveJobs = jobs.some(job => ACTIVE_STATES.has(job.status));
  /** Job IDs the user dismissed or cancelled; never re-add from poll/realtime unless state meaningfully changes (we don't re-add). */
  const hiddenJobIdsRef = useRef<Set<string>>(new Set());

  // Persist across navigations with size limits
  useEffect(() => {
    const toStore = jobs.slice(0, MAX_STORED_JOBS);
    sessionStorage.setItem('vrr.active', JSON.stringify(toStore));
  }, [jobs]);

  // Reset activeIndex if current job doesn't exist
  useEffect(() => {
    if (activeIndex >= jobs.length && jobs.length > 0) {
      setActiveIndex(0);
    }
  }, [jobs.length, activeIndex]);

  // Cached job rows can belong to the prior session. Drop them until the
  // current user has a fully resolved, data-bearing access context.
  useEffect(() => {
    if (canAccessAppData) return;
    setJobs([]);
    setActiveIndex(0);
    sessionStorage.removeItem('vrr.active');
  }, [canAccessAppData, session?.user?.id]);

  // One refresh owner for boot, active-job fallback, reconnect and visible return.
  useEffect(() => {
    if (!canAccessAppData || !session?.user?.id) return;
    const userId = session.user.id;
    let disposed = false;
    let connected = false;
    let receivedStatus = false;
    let mergeTimer: ReturnType<typeof setTimeout> | undefined;
    const incoming: JobRowUpdate[] = [];
    const poller = startVisiblePolling({
      delay: () => jobsRef.current.some(job => ACTIVE_STATES.has(job.status)) ? 5_000 : connected ? 60_000 : 15_000,
      poll: async () => {
        const startedAt = Date.now();
        const { data, error } = await fetchVenueResearchRuns({ requestedBy: userId });
        if (error) throw error;
        if (disposed) return;
        setJobs(prev => {
          const ids = new Set(data.map(job => job.job_id));
          const surviving = prev.filter(job => job.job_kind === 'item_lookup' || ids.has(job.job_id) || Date.parse(job.updated_at) >= startedAt);
          const next = mergeJobs(surviving, data as JobRowUpdate[])
            .filter(job => !hiddenJobIdsRef.current.has(job.job_id)).slice(0, MAX_STORED_JOBS);
          return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
        });
      },
      onError: error => console.error('[jobhub] refresh failed:', error),
    });
    pollingRef.current = poller;
    const channel = subscribeToVenueResearchRuns({
      requestedBy: userId,
      handler: payload => {
        if (disposed) return;
        const row = (payload as any).new as JobRowUpdate | undefined;
        if (!row?.job_id) { void poller.refresh(); return; }
        incoming.push(row);
        if (mergeTimer !== undefined) return;
        mergeTimer = setTimeout(() => {
          mergeTimer = undefined;
          if (disposed) return;
          const updates = incoming.splice(0);
          setJobs(prev => mergeJobs(prev, updates)
            .filter((job, index) => (index === 0 || isActive(job)) && !hiddenJobIdsRef.current.has(job.job_id))
            .slice(0, MAX_STORED_JOBS));
        }, 0);
      },
      onStatusChange: status => {
        if (disposed) return;
        const wasConnected = connected;
        connected = status === 'SUBSCRIBED';
        if (receivedStatus && connected && !wasConnected) void poller.refresh();
        else poller.reschedule();
        receivedStatus = true;
      },
    });
    return () => {
      disposed = true;
      poller.stop();
      pollingRef.current = null;
      if (mergeTimer !== undefined) clearTimeout(mergeTimer);
      channel.unsubscribe();
    };
  }, [canAccessAppData, session?.user?.id]);

  useEffect(() => { pollingRef.current?.reschedule(); }, [hasActiveJobs]);

  function next() { setActiveIndex(i => Math.min(jobs.length - 1, i + 1)); }
  function prev() { setActiveIndex(i => Math.max(0, i - 1)); }

  async function dismiss(jobId: string) {
    const prevJobs = jobs;
    const targetJob = prevJobs.find(j => j.job_id === jobId);
    const skipApi = targetJob?.job_kind === 'item_lookup';
    hiddenJobIdsRef.current.add(jobId);
    setJobs(prev => prev.filter(j => j.job_id !== jobId));
    setActiveIndex(prev => {
      const idx = prevJobs.findIndex(j => j.job_id === jobId);
      if (idx < 0) return prev;
      const newLength = prevJobs.length - 1;
      if (newLength <= 0) return 0;
      return Math.max(0, Math.min(prev >= idx ? prev - 1 : prev, newLength - 1));
    });

    try {
      if (!skipApi) {
        const res = await apiFetch(`/ai/venue-jobs/${jobId}/dismiss`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (!res.ok && res.status !== 404) throw new Error('dismiss failed');
      }
    } catch (err) {
      console.error('[jobhub] dismiss error:', err);
      hiddenJobIdsRef.current.delete(jobId);
      setJobs(prevJobs);
    }
  }

  async function cancel(job: JobRow): Promise<boolean> {
    if (job.job_kind === 'item_lookup') return true;
    const jobId = job.job_id;
    const prevJobs = jobs;
    hiddenJobIdsRef.current.add(jobId);
    setJobs(prev => prev.filter(j => j.job_id !== jobId));
    setActiveIndex(prev => {
      const idx = prevJobs.findIndex(j => j.job_id === jobId);
      if (idx < 0) return prev;
      const newLength = prevJobs.length - 1;
      if (newLength <= 0) return 0;
      return Math.max(0, Math.min(prev >= idx ? prev - 1 : prev, newLength - 1));
    });

    try {
      const response = await apiFetch(`/ai/venue-jobs/${jobId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) return true;
      hiddenJobIdsRef.current.delete(jobId);
      setJobs(prevJobs);
      return false;
    } catch (error) {
      hiddenJobIdsRef.current.delete(jobId);
      setJobs(prevJobs);
      return false;
    }
  }

  // Convenience helper for callers so they don't have to hand-roll merge logic
  function upsertJob(job: JobRowUpdate) {
    setJobs(prev => {
      const list = prev ?? [];
      const i = list.findIndex(j => j.job_id === job.job_id);
      if (i >= 0) {
        const copy = list.slice();
        copy[i] = normaliseJob(job, copy[i]);
        return copy;
      }
      // prepend newest; let the existing size/filters handle trimming
      return [normaliseJob(job), ...list].slice(0, MAX_STORED_JOBS);
    });
  }

  const value = useMemo<Ctx>(
  () => ({ jobs, setJobs, upsertJob, activeIndex, next, prev, cancel, dismiss }),
  [jobs, activeIndex]
);


  return <JobsCtx.Provider value={value}>{children}</JobsCtx.Provider>;
};

export function mergeJobs(prev: JobRow[], incoming: JobRowUpdate[]): JobRow[] {
  const map = new Map(prev.map(j => [j.job_id, j]));
  for (const update of incoming) {
    const existing = map.get(update.job_id);
    if (existing && update.updated_at && existing.updated_at) {
      if (new Date(update.updated_at) < new Date(existing.updated_at)) {
        continue;
      }
    }
    const patch: JobRowUpdate = { job_id: update.job_id };
    (Object.keys(update) as (keyof JobRowUpdate)[]).forEach(key => {
      const value = update[key];
      if (value === null || value === undefined) return;
      (patch as any)[key] = value;
    });
    const normalised = normaliseJob(patch, existing);
    map.set(normalised.job_id, normalised);
  }
  // Order: newest first by updated_at
  const arr = Array.from(map.values()).sort((a,b) => {
    const dt = +new Date(b.updated_at) - +new Date(a.updated_at);
    if (dt !== 0) return dt;
    // Tie-break deterministically by job_id (desc) to keep stable order
    return b.job_id.localeCompare(a.job_id);
  });
  return arr;
}

export function useJobs() {
  const ctx = useContext(JobsCtx);
  if (!ctx) throw new Error('useJobs must be used within JobHubProvider');
  return ctx;
}
